import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  ShieldAlert,
  Radar,
  Server,
  BarChart2,
  Building2,
  Users,
  ShieldCheck,
  BookUser,
  Menu,
  type LucideIcon,
} from 'lucide-react';
import { ROUTES } from '@/config/routes';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';

interface BottomNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  /**
   * Path prefixes that mark this tab active. Several tabs live under /nirp, so a
   * single broad prefix would light up more than one at once — Incidents and
   * Threat were both matching /nirp. Each tab lists its own specific prefixes,
   * and the active check keeps only the longest match so exactly one wins.
   */
  matchPrefixes?: string[];
  requiredPermission?: string;
}

/**
 * The candidate destinations for the mobile tab bar, in priority order.
 *
 * Mirrors the sidebar's sections and their permissions, so a user only ever
 * sees tabs they can actually open — a viewer gets fewer than an org admin.
 * The bar shows the first four this user is allowed, then always a "More" tab
 * that opens the full navigation drawer, matching the reference layout.
 */
const CANDIDATES: BottomNavItem[] = [
  { id: 'home', label: 'Home', icon: Home, href: ROUTES.HOME },
  {
    id: 'incident',
    label: 'Incidents',
    icon: ShieldAlert,
    href: ROUTES.NIRP_ORG_DASHBOARD,
    // Incident routes only — NOT the whole /nirp namespace, which also holds
    // threat-intel and messages.
    matchPrefixes: [
      ROUTES.NIRP_ORG_DASHBOARD,
      ROUTES.NIRP_SUBMIT_INCIDENT,
      ROUTES.NIRP_MY_INCIDENTS,
      ROUTES.NIRP_ADMIN_DASHBOARD,
      ROUTES.NIRP_ADMIN_INCIDENTS,
    ],
  },
  {
    id: 'threat',
    label: 'Threat',
    icon: Radar,
    href: ROUTES.NIRP_THREAT_FEED,
    matchPrefixes: [
      ROUTES.NIRP_THREAT_FEED,
      ROUTES.NIRP_THREAT_DASHBOARD,
      ROUTES.NIRP_ADMIN_THREAT_INTEL,
    ],
  },
  {
    id: 'assets',
    label: 'Assets',
    icon: Server,
    href: ROUTES.ASSETS,
    matchPrefixes: ['/assets'],
    requiredPermission: 'assets:read',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart2,
    href: ROUTES.ANALYTICS,
    matchPrefixes: [ROUTES.ANALYTICS],
    requiredPermission: 'analytics:read',
  },
  {
    id: 'organizations',
    label: 'Orgs',
    icon: Building2,
    href: ROUTES.ADMIN_ORGANIZATIONS,
    matchPrefixes: [ROUTES.ADMIN_ORGANIZATIONS],
    requiredPermission: 'platform:orgs-manage',
  },
  {
    id: 'team',
    label: 'Team',
    icon: Users,
    href: ROUTES.TEAM,
    matchPrefixes: [ROUTES.TEAM],
    requiredPermission: 'users:read',
  },
  {
    id: 'roles',
    label: 'Security',
    icon: ShieldCheck,
    href: ROUTES.ADMIN_ROLES,
    matchPrefixes: [ROUTES.ADMIN_ROLES],
    requiredPermission: 'roles:read',
  },
  {
    id: 'directory',
    label: 'Directory',
    icon: BookUser,
    href: ROUTES.DIRECTORY,
    matchPrefixes: [ROUTES.DIRECTORY],
  },
];

/** How many real tabs sit alongside the always-present "More". */
const MAX_TABS = 4;

interface MobileBottomNavProps {
  /** Opens the full navigation drawer (the sidebar). */
  onOpenMore: () => void;
}

export function MobileBottomNav({ onOpenMore }: MobileBottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  const tabs = useMemo(
    () =>
      CANDIDATES.filter(
        (t) => !t.requiredPermission || hasPermission(t.requiredPermission),
      ).slice(0, MAX_TABS),
    [hasPermission],
  );

  // Exactly one tab may be active. Several sections live under /nirp, so a
  // simple "does the path start with my prefix" test lit up more than one at a
  // time. Instead, score every visible tab by its longest matching prefix and
  // let the single best score win — a threat page beats the incident tab
  // because its prefix is the more specific match.
  const activeId = useMemo(() => {
    const path = location.pathname;
    let bestId: string | null = null;
    let bestLen = 0;
    for (const t of tabs) {
      const prefixes = t.matchPrefixes ?? [t.href];
      for (const p of prefixes) {
        if ((path === p || path.startsWith(p)) && p.length > bestLen) {
          bestLen = p.length;
          bestId = t.id;
        }
      }
    }
    return bestId;
  }, [tabs, location.pathname]);

  return (
    <nav
      // Sits above content, respects the iOS home-indicator inset, and is hidden
      // on desktop where the sidebar takes over.
      className="fixed inset-x-0 bottom-0 z-40 flex md:hidden border-t border-border bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
      aria-label="Primary"
    >
      {tabs.map((t) => {
        const active = t.id === activeId;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            onClick={() => navigate(t.href)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium transition-colors',
              active
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-5 w-5" strokeWidth={active ? 2.4 : 1.8} />
            <span className="leading-none">{t.label}</span>
          </button>
        );
      })}

      <button
        onClick={onOpenMore}
        className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <Menu className="h-5 w-5" strokeWidth={1.8} />
        <span className="leading-none">More</span>
      </button>
    </nav>
  );
}
